import { Box, Text } from '@factorialco/deprecated-design-system'
import {
  BillingAddonReferenceFragment,
  UpsellLandingPage,
  UpsellLandingPagesQuery,
} from 'generated/resources/graphql'

import { OneLandingButton } from 'modules/upsell/components/OneLanding/Button'

type UpsellSection = NonNullable<
  NonNullable<
    NonNullable<UpsellLandingPagesQuery['upsell']['landingPagesConnection']['edges'][0]>['node']
  >['sections']
>[0]

interface GradientMediaContainerProps {
  children: React.ReactNode
  maxWidth?: string
  className?: string
}

const GradientMediaContainer = ({ children, maxWidth, className }: GradientMediaContainerProps) => {
  return (
    <Box
      className={className}
      style={{
        maxWidth,
        background:
          'linear-gradient(98.39deg, rgba(249, 115, 22, 0.49) 0%, rgba(229, 25, 67, 0.49) 20%, rgba(229, 25, 67, 0.49) 49.97%, rgba(229, 25, 67, 0.49) 80%, rgba(164, 165, 222, 0.49) 100%)',
        padding: '2px',
        borderRadius: '20px',
        boxShadow:
          '0 0 25px rgba(249, 115, 22, 0.15), 0 0 15px rgba(229, 25, 67, 0.1), 0 0 35px rgba(164, 165, 222, 0.08)',
        overflow: 'hidden',
      }}
    >
      <Box borderRadius={{ all: 'abs016' }} overflowX="hidden">
        {children}
      </Box>
    </Box>
  )
}

const MediaBox = ({
  src,
  text,
  highlightedText,
  buttonLabel,
  billingAddon,
  moduleName,
  alreadyInOneWaitlist,
}: {
  src: string | null | undefined
  text: string | null | undefined
  highlightedText: string | null | undefined
  buttonLabel: string | null | undefined
  billingAddon: BillingAddonReferenceFragment | null
  moduleName: string
  alreadyInOneWaitlist: boolean
}) => {
  const isVideo = src?.match(/\.(mp4|webm|ogg|mov|avi)$/i)
  return (
    <Box
      overflowX="hidden"
      style={{
        position: 'relative',
        overflow: 'hidden',
        boxShadow:
          '0 0 25px rgba(249, 115, 22, 0.15), 0 0 15px rgba(229, 25, 67, 0.1), 0 0 35px rgba(164, 165, 222, 0.08)',
        borderRadius: '24px',
      }}
    >
      {isVideo ? (
        <video src={src || ''} autoPlay muted loop style={{ borderRadius: '8px', width: '100%' }} />
      ) : (
        <img
          src={src || ''}
          alt=""
          style={{
            borderRadius: '16px',
            width: '100%',
            overflow: 'hidden',
          }}
        />
      )}
      {text && (
        <Box
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            textAlign: 'center',
            zIndex: '1',
            maxWidth: '500px',
            justifyContent: 'center',
            alignItems: 'center',
          }}
          gap="s32"
        >
          <Box flexDirection="column" alignItems="center">
            <div
              className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl"
              style={{
                fontFamily: 'Inter',
                fontStyle: 'normal',
                fontWeight: 700,
                textAlign: 'center',
                maxWidth: '600px',
                wordBreak: 'break-word',
                lineHeight: '1.2',
                letterSpacing: '-0.02em',
              }}
            >
              {highlightedText && (
                <span
                  style={{
                    background:
                      'linear-gradient(270deg, rgba(161, 173, 229, 0.70) 0%, rgba(229, 25, 67, 0.70) 50%, rgba(229, 86, 25, 0.70) 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text',
                    marginRight: '12px',
                  }}
                >
                  {highlightedText}
                </span>
              )}
              <span style={{ color: 'black' }}>{text}</span>
            </div>
          </Box>
          {buttonLabel && !alreadyInOneWaitlist && (
            <OneLandingButton
              buttonLabel={buttonLabel}
              variant="primary"
              componentName="footer-section"
              billingAddon={billingAddon}
              moduleName={moduleName}
            />
          )}
        </Box>
      )}
    </Box>
  )
}

const FeaturesSection = ({
  section,
  billingAddon,
  moduleName,
  alreadyInOneWaitlist,
}: {
  section: UpsellSection
  billingAddon: BillingAddonReferenceFragment | null
  moduleName: string
  alreadyInOneWaitlist: boolean
  content: UpsellLandingPage
}) => {
  return (
    <Box className="mx-auto" alignItems="center" textAlign="center" gap="s80" paddingTop="s80">
      <Box maxWidth="s600" gap="s8">
        {section.title && (
          <Text size="700" color="black" weight="bold">
            {section.title}
          </Text>
        )}
        {section.description && (
          <Text size="400" color="black">
            {section.description}
          </Text>
        )}
      </Box>

      <Box className="mt-10">
        {section.features && (
          <Box
            className="grid grid-cols-1 md:grid-cols-1 lg:grid-cols-3 md:gap-10 lg:gap-14"
            style={{ gap: '50px' }}
          >
            {section.features.map((feature) => (
              <Box
                key={feature.title}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                }}
                alignItems="flexStart"
                justifyContent="flexStart"
                textAlign="left"
              >
                <Text size="200" color="black" weight="bold">
                  {feature.title}
                </Text>
                <Text size="200" color="black">
                  {feature.description}
                </Text>
              </Box>
            ))}
          </Box>
        )}
      </Box>
      {section.media?.url && (
        <MediaBox
          src={section.media?.url}
          text={section.mediaText}
          highlightedText={section.highlightedMediaText}
          buttonLabel={section.buttonLabel}
          billingAddon={billingAddon}
          moduleName={moduleName}
          alreadyInOneWaitlist={alreadyInOneWaitlist}
        />
      )}
    </Box>
  )
}

export { GradientMediaContainer }
export default FeaturesSection
